'use client';

/**
 * Standardized error handling utilities for the application
 * Provides consistent error processing and user-friendly messages
 */

// Error types for better categorization
export enum ErrorCategory {
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  NETWORK = 'network',
  FIREBASE = 'firebase',
  PAYMENT = 'payment',
  UNKNOWN = 'unknown',
}

// Structured error interface
export interface ApplicationError {
  message: string;
  category: ErrorCategory;
  originalError?: unknown;
  userFriendlyMessage: string;
  shouldShowToUser: boolean;
  actionable?: boolean;
}

/**
 * Extracts a user-friendly error message from any error type
 * Handles Error objects, strings, and unknown error types safely
 */
export function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }

  return 'An unexpected error occurred';
}

/**
 * Categorizes errors based on their type and message content
 * Returns appropriate error category for better handling
 */
export function categorizeError(error: unknown): ErrorCategory {
  const errorMessage = extractErrorMessage(error).toLowerCase();

  // Firebase specific errors
  if (
    errorMessage.includes('firebase') ||
    errorMessage.includes('firestore') ||
    errorMessage.includes('permission-denied')
  ) {
    return ErrorCategory.FIREBASE;
  }

  // Network errors
  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('connection')) {
    return ErrorCategory.NETWORK;
  }

  // Authentication errors
  if (
    errorMessage.includes('authentication') ||
    errorMessage.includes('unauthorized') ||
    errorMessage.includes('login')
  ) {
    return ErrorCategory.AUTHENTICATION;
  }

  // Authorization errors
  if (
    errorMessage.includes('authorization') ||
    errorMessage.includes('forbidden') ||
    errorMessage.includes('access denied')
  ) {
    return ErrorCategory.AUTHORIZATION;
  }

  // Validation errors
  if (errorMessage.includes('validation') || errorMessage.includes('invalid') || errorMessage.includes('required')) {
    return ErrorCategory.VALIDATION;
  }

  // Payment errors
  if (errorMessage.includes('payment') || errorMessage.includes('stripe') || errorMessage.includes('paypal')) {
    return ErrorCategory.PAYMENT;
  }

  return ErrorCategory.UNKNOWN;
}

/**
 * Generates user-friendly error messages based on error category
 * Provides actionable feedback when possible
 */
export function generateUserFriendlyMessage(category: ErrorCategory, originalMessage: string): string {
  const categoryMessages: Record<ErrorCategory, string> = {
    [ErrorCategory.VALIDATION]: 'Please check your input and try again.',
    [ErrorCategory.AUTHENTICATION]: 'Please sign in to continue.',
    [ErrorCategory.AUTHORIZATION]: "You don't have permission to perform this action.",
    [ErrorCategory.NETWORK]: 'Please check your internet connection and try again.',
    [ErrorCategory.FIREBASE]: "We're experiencing technical difficulties. Please try again later.",
    [ErrorCategory.PAYMENT]: 'There was an issue processing your payment. Please try again.',
    [ErrorCategory.UNKNOWN]: 'Something went wrong. Please try again.',
  };

  // For validation errors, try to preserve the original message if it's user-friendly
  if (category === ErrorCategory.VALIDATION && originalMessage.length < 100 && !originalMessage.includes('Error:')) {
    return originalMessage;
  }

  return categoryMessages[category];
}

/**
 * Determines if an error should be displayed to the user
 * Some errors are for debugging only and shouldn't be shown to users
 */
export function shouldShowErrorToUser(category: ErrorCategory): boolean {
  const userVisibleCategories = [
    ErrorCategory.VALIDATION,
    ErrorCategory.AUTHENTICATION,
    ErrorCategory.AUTHORIZATION,
    ErrorCategory.NETWORK,
    ErrorCategory.PAYMENT,
  ];

  return userVisibleCategories.includes(category);
}

/**
 * Main error processing function that creates standardized ApplicationError
 * Use this as the primary entry point for error handling
 */
export function processError(error: unknown): ApplicationError {
  const message = extractErrorMessage(error);
  const category = categorizeError(error);
  const userFriendlyMessage = generateUserFriendlyMessage(category, message);
  const shouldShowToUser = shouldShowErrorToUser(category);

  return {
    message,
    category,
    originalError: error,
    userFriendlyMessage,
    shouldShowToUser,
    actionable: category !== ErrorCategory.UNKNOWN,
  };
}

/**
 * Logs errors with appropriate level based on category
 * Critical errors are logged as errors, others as warnings
 */
export function logError(applicationError: ApplicationError): void {
  const { category, message, originalError } = applicationError;

  const criticalCategories = [ErrorCategory.FIREBASE, ErrorCategory.PAYMENT, ErrorCategory.UNKNOWN];

  const logData = {
    category,
    message,
    timestamp: new Date().toISOString(),
  };

  if (criticalCategories.includes(category)) {
    console.error('Application Error:', logData, originalError);
  } else {
    console.warn('Application Warning:', logData, originalError);
  }
}

/**
 * Hook for consistent error handling in React components
 * Returns processed error and helper functions
 */
export function useErrorHandler() {
  const handleError = (error: unknown): ApplicationError => {
    const processedError = processError(error);
    logError(processedError);
    return processedError;
  };

  const handleAsyncError = async <T>(
    asyncOperation: () => Promise<T>,
  ): Promise<{ data?: T; error?: ApplicationError }> => {
    try {
      const data = await asyncOperation();
      return { data };
    } catch (error) {
      const processedError = handleError(error);
      return { error: processedError };
    }
  };

  return {
    handleError,
    handleAsyncError,
    extractErrorMessage,
    processError,
  };
}
